// frontend/packages/business-console/src/components/BillingHistory.jsx
//
// Tenant-facing "Your UpRyzze payments" card. Self-fetching, read-only:
// shows the active subscription summary + a list of UpRyzze invoices with
// status, amount, date, and links to the Stripe-hosted invoice / PDF
// (the receipts). Sits on the Billing & Reminders page.
//
// Data is display-safe (no Stripe ids) — see businessBillingController.js.
// Degrade-quiet: a tenant with no billing history yet sees a friendly
// empty state, not an error.

import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Link,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';

import { getBillingHistory } from '../services/billingHistoryService';

const STATUS_COLOR = {
  paid: 'success',
  open: 'warning',
  draft: 'default',
  void: 'default',
  uncollectible: 'error',
};

function formatMoney(amount, currency) {
  const n = Number(amount) || 0;
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: (currency || 'usd').toUpperCase(),
    }).format(n);
  } catch {
    return `$${n.toFixed(2)}`;
  }
}

function formatDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return '';
  }
}

function SubscriptionSummary({ subscription }) {
  if (!subscription) return null;
  const { status, amount, currency, interval, currentPeriodEnd } = subscription;
  return (
    <Box sx={{ mb: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          Subscription
        </Typography>
        <Chip
          size="small"
          color={status === 'active' || status === 'trialing' ? 'success' : 'default'}
          label={status}
        />
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {formatMoney(amount, currency)}/{interval}
        </Typography>
      </Stack>
      {currentPeriodEnd ? (
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {subscription.cancelAtPeriodEnd ? 'Ends' : 'Renews'}{' '}
          {formatDate(currentPeriodEnd)}
        </Typography>
      ) : null}
      <Divider sx={{ mt: 2 }} />
    </Box>
  );
}

function InvoiceRow({ invoice }) {
  const { number, status, amountDue, amountPaid, currency, issuedAt } = invoice;
  const paid = status === 'paid';
  return (
    <Stack
      direction="row"
      spacing={1.5}
      alignItems="center"
      sx={{ py: 1.25 }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {number ? `Invoice ${number}` : 'Invoice'}
          </Typography>
          <Chip
            size="small"
            color={STATUS_COLOR[status] || 'default'}
            label={status}
          />
        </Stack>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {formatDate(issuedAt)}
          {invoice.description ? ` • ${invoice.description}` : ''}
        </Typography>
      </Box>
      <Box sx={{ textAlign: 'right' }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {formatMoney(paid ? amountPaid : amountDue, currency)}
        </Typography>
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          {invoice.hostedInvoiceUrl ? (
            <Link
              href={invoice.hostedInvoiceUrl}
              target="_blank"
              rel="noopener noreferrer"
              variant="caption"
              sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}
            >
              View <OpenInNewRoundedIcon sx={{ fontSize: 12 }} />
            </Link>
          ) : null}
          {invoice.invoicePdfUrl ? (
            <Link
              href={invoice.invoicePdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              variant="caption"
              sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}
            >
              <PictureAsPdfOutlinedIcon sx={{ fontSize: 12 }} /> PDF
            </Link>
          ) : null}
        </Stack>
      </Box>
    </Stack>
  );
}

export default function BillingHistory() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [data, setData] = useState(null);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await getBillingHistory();
        if (active) setData(res);
      } catch {
        if (active) setError(true);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const invoices = data?.invoices || [];
  const visible = showAll ? invoices : invoices.slice(0, 5);

  return (
    <Paper
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        p: 2.5,
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
        <ReceiptLongOutlinedIcon
          fontSize="small"
          sx={{ color: 'text.secondary' }}
        />
        <Typography variant="body1" sx={{ fontWeight: 600 }}>
          Your UpRyzze payments
        </Typography>
      </Stack>

      {loading ? (
        <Stack direction="row" spacing={1} alignItems="center">
          <CircularProgress size={16} />
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Loading…
          </Typography>
        </Stack>
      ) : error ? (
        <Alert severity="info" sx={{ mt: 0.5 }}>
          Billing history isn&apos;t available right now.
        </Alert>
      ) : (
        <>
          <SubscriptionSummary subscription={data?.subscription} />
          {invoices.length === 0 ? (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              No invoices yet. Charges from UpRyzze will show up here with
              receipts you can download.
            </Typography>
          ) : (
            <Box>
              {visible.map((inv, i) => (
                <React.Fragment key={`${inv.number || 'inv'}-${i}`}>
                  {i > 0 ? <Divider /> : null}
                  <InvoiceRow invoice={inv} />
                </React.Fragment>
              ))}
              {invoices.length > 5 ? (
                <Box sx={{ mt: 1 }}>
                  <Button size="small" onClick={() => setShowAll((v) => !v)}>
                    {showAll ? 'Show less' : `Show all ${invoices.length}`}
                  </Button>
                </Box>
              ) : null}
            </Box>
          )}
        </>
      )}
    </Paper>
  );
}
